import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useCanvasStore, Item } from '@/stores/canvasStore';
import { usePartnrStore } from '@/stores/partnrStore';
import { usePartnrBetaStore } from '@/stores/partnrBetaStore';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { PartnrComposeBox } from './PartnrComposeBox';
import { 
  Search, 
  Plus, 
  ExternalLink, 
  X, 
  StickyNote, 
  Type, 
  FileText, 
  Image, 
  File,
  History,
  Link as LinkIcon,
  ArrowRight,
  Folder,
  LocateFixed,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PartnrChatHistory } from './PartnrChatHistory';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SyncChoiceModal } from '@/components/canvas/SyncChoiceModal';
import { TweetPreviewCard, isTweetItem } from '@/components/items/TweetPreviewCard';
import { getLinkResultTitle, getSearchResultThumbnail, getSearchableFields } from '@/lib/searchResultPreview';
import { searchBoards, type BoardSearchResult } from '@/lib/boardSearch';
import { fetchAllPages } from '@/lib/supabasePages';
import { getFolderColorFromValue } from '@/lib/folderColors';
import { detectBarIntent, type BarIntent } from '@/lib/partnrIntent';
import { PartnrMark } from '@/components/partnr/kit/PartnrMark';
import { isPartnrV2Enabled } from '@/lib/partnr/flag';
import { usePartnrPanelStore } from '@/stores/partnrPanelStore';

/* ── Seek: the search circle that becomes the search field ──
   One object, not two: a single box whose width is the state, with the lens
   and the field inside it the whole time. The lens sits at a fixed inset that
   centres it in the closed circle and aligns it as the open field's icon, so
   it travels left only because the box grows around it. The toolbar centres
   the bar, so the box grows from its middle. A press compresses it for a beat
   before it expands, so it reads as pushed rather than triggered. */
const SEEK_SHUT = 40; // the dock's button size: a circle when closed
const SEEK_OPEN = 320; // the old 36px icon + 4px gap + 280px field
const SEEK_PRESS_MS = 90;
const SEEK_SPRING = { type: 'spring', stiffness: 500, damping: 38 } as const;

interface SearchResult extends Item {
  boardName?: string;
  boardColor?: string;
}

export function PartnrSearchBar() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  // Boards and folders whose names match, shown above the items
  const [boardResults, setBoardResults] = useState<BoardSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SearchResult | null>(null);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  // The text the current `results` were found for
  const [searchedText, setSearchedText] = useState('');
  // Set by Tab or the intent pill. Holds only while the detected intent stays
  // what it was when the user flipped it, so retyping or pasting starts fresh.
  const [intentOverride, setIntentOverride] = useState<{ intent: BarIntent; over: BarIntent } | null>(null);
  const reduceMotion = useReducedMotion();
  const [syncModalState, setSyncModalState] = useState<{
    item: SearchResult;
    position: { x: number; y: number };
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const composeRef = useRef<HTMLTextAreaElement>(null);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  // Seek: open while focused (or holding a query / results), pressed for one beat before opening
  const [seekFocused, setSeekFocused] = useState(false);
  const [seekPressed, setSeekPressed] = useState(false);
  // The lens thickens for a burst of typing, not per key
  const [seekTyping, setSeekTyping] = useState(false);
  const seekBeat = useRef<number>();
  const seekRest = useRef<number>();
  useEffect(() => () => {
    window.clearTimeout(seekBeat.current);
    window.clearTimeout(seekRest.current);
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);
  const previewModalRef = useRef<HTMLDivElement>(null);

  const { 
    boards, 
    currentBoardId, 
    createItem, 
    panX, 
    panY, 
    zoom,
    setViewMode,
    navigateToBoard,
    openItemOnCanvas,
    fetchBoards,
    items,
    selectItem,
    clearSelection,
    setPan,
    selectedItemIds: canvasSelectedItemIds,
  } = useCanvasStore();

  const {
    isPartnrMode,
    setPartnrMode,
    isHistoryOpen,
    toggleHistoryPanel,
    createChat,
    isStreaming,
    partnrSelectedItemIds,
    clearPartnrSelection,
    togglePartnrItemSelection,
    setSearchEscalationContext,
    searchEscalationContext,
  } = usePartnrStore();

  // Partnr 2: hand messages to the side panel instead of the chat window
  const [partnrV2] = useState(isPartnrV2Enabled);
  const panelStreaming = usePartnrPanelStore((s) => s.liveMessage !== null);
  const partnrBusy = partnrV2 ? panelStreaming : isStreaming;

  // Get Partnr-selected items
  const selectedItems = items.filter(item => partnrSelectedItemIds.has(item.id));
  
  // Partnr beta state
  const { isPartnrBetaEnabled } = usePartnrBetaStore();
  
  // Canvas-selected items still on the board: an undo can remove them while
  // they stay selected, and the count should not include them
  const canvasSelectionCount = useMemo(
    () => items.reduce((n, item) => (canvasSelectedItemIds.has(item.id) ? n + 1 : n), 0),
    [items, canvasSelectedItemIds],
  );

  // Check if canvas has selected items (for glow effect) - only when beta is enabled
  const hasCanvasSelection = isPartnrBetaEnabled && canvasSelectionCount > 0;

  // Search or Partnr? Live results only count once they match what's typed.
  const searchText = detectBarIntent(query).text;
  const resultCount = !isSearching && searchedText === searchText ? results.length + boardResults.length : undefined;
  const detected = detectBarIntent(query, { resultCount });
  const autoIntent: BarIntent = hasCanvasSelection && !detected.forced ? 'partnr' : detected.intent;
  const barIntent: BarIntent = !isPartnrBetaEnabled
    ? 'search'
    : intentOverride?.over === autoIntent ? intentOverride.intent : autoIntent;
  const showIntentPill = isPartnrBetaEnabled && query.trim().length > 0;

  // Away with nothing typed and it closes; with a query or open results it stays,
  // so a click on a result never lands on a bar that shrank under it
  const seekOpen = seekFocused || query.length > 0 || isResultsOpen;
  const openSeek = () => {
    if (seekOpen) return;
    window.clearTimeout(seekBeat.current);
    if (reduceMotion) {
      inputRef.current?.focus();
      return;
    }
    setSeekPressed(true);
    seekBeat.current = window.setTimeout(() => {
      setSeekPressed(false);
      // Focus lands with the expansion, so the caret is there when the field arrives
      inputRef.current?.focus();
    }, SEEK_PRESS_MS);
  };
  const markTyping = () => {
    setSeekTyping(true);
    window.clearTimeout(seekRest.current);
    seekRest.current = window.setTimeout(() => setSeekTyping(false), 340);
  };

  // Dynamic suggestions based on escalation context
  const escalationSuggestions = useMemo(() => {
    if (!searchEscalationContext) return null;
    
    const { originalQuery, resultCount } = searchEscalationContext;
    
    if (resultCount === 0) {
      return [
        `Help me find information about "${originalQuery}"`,
        `What do you know about ${originalQuery}?`,
        `Suggest related topics to ${originalQuery}`,
        `Help me create content about ${originalQuery}`,
      ];
    }
    
    return [
      `Summarize what I found about "${originalQuery}"`,
      `Find connections between these results`,
      `What's missing from my research on "${originalQuery}"?`,
      `Turn these findings into an action plan`,
    ];
  }, [searchEscalationContext]);

  // Helper to get item preview text
  const getItemPreviewText = (item: Item): string => {
    const content = item.content as Record<string, unknown>;
    switch (item.item_type) {
      case 'sticky':
        return (content.text as string)?.substring(0, 20) || 'Sticky Note';
      case 'text_card':
        return (content.title as string)?.substring(0, 20) || 'Text Card';
      case 'document':
        return (content.title as string)?.substring(0, 20) || 'Document';
      case 'image':
        return (content.alt as string)?.substring(0, 20) || 'Image';
      case 'file':
        return (content.filename as string)?.substring(0, 20) || 'File';
      case 'link':
        return getLinkResultTitle(item).substring(0, 20);
      default:
        return 'Item';
    }
  };

  // Fetch boards on mount
  useEffect(() => {
    fetchBoards();
  }, [fetchBoards]);

  // Leaving Partnr mode unmounts the compose box; start closed next time
  useEffect(() => {
    if (!isPartnrMode) setIsComposeOpen(false);
  }, [isPartnrMode]);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsResultsOpen(false);
        setSelectedItem(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // The preview modal is portaled to <body>, so it is outside containerRef
      if (previewModalRef.current?.contains(target)) return;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsResultsOpen(false);
        setSelectedItem(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search function with fuzzy matching and scoring
  const searchItems = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setBoardResults([]);
      setSearchedText('');
      return;
    }

    setIsSearching(true);

    // RLS also returns rows from anyone's publicly shared boards; search is for your own
    const { data: auth } = await supabase.auth.getSession();
    const ownerId = auth.session?.user.id;

    // Every row, not the API's first 1000: older items and folder cards live past it
    const { data: rows, error } = await fetchAllPages(
      (from, to, withCount) => {
        let page = supabase.from('items').select('*', withCount ? { count: 'exact' } : undefined);
        if (ownerId) page = page.eq('user_id', ownerId);
        return page
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .range(from, to);
      },
      (row) => row.id,
    );

    if (error) {
      console.error('Error searching items:', error);
      setIsSearching(false);
      return;
    }

    const q = searchQuery.toLowerCase().trim();
    const queryWords = q.split(/\s+/).filter(w => w.length > 1);

    // Score each item based on multiple relevance signals
    const scored = rows.map(item => {
      if (item.item_type === 'board_link') return { item, score: 0 };
      
      const fields = getSearchableFields(item as Item);

      const fullText = fields.join(' ');
      let score = 0;

      // Exact phrase match (highest weight)
      if (fullText.includes(q)) {
        score += 100;
      }

      // Individual word matches
      for (const word of queryWords) {
        for (let i = 0; i < fields.length; i++) {
          if (fields[i].includes(word)) {
            score += 10;
            // Bonus for title/filename match
            if (i === 1 || i === 3) {
              score += 15;
            }
          }
        }
      }

      // Word-start matches (e.g., "des" matches "design")
      for (const word of queryWords) {
        const wordStartRegex = new RegExp(`\\b${word}`, 'i');
        if (wordStartRegex.test(fullText)) {
          score += 5;
        }
      }

      // Item type relevance boost (recent items score higher)
      const ageMs = Date.now() - new Date(item.created_at).getTime();
      const ageDays = ageMs / (1000 * 60 * 60 * 24);
      if (ageDays < 7) score += 3;
      if (ageDays < 1) score += 5;

      return { item, score };
    });

    // Filter items with score > 0, sort by score descending, limit to 20
    const results = scored
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 20)
      .map(s => {
        const board = boards.find(b => b.id === s.item.board_id);
        return {
          ...s.item,
          item_type: s.item.item_type as Item['item_type'],
          boardName: board?.name,
          boardColor: board?.icon_color,
        };
      });

    setResults(results);
    setBoardResults(searchBoards(searchQuery, boards, rows, ownerId));
    setSearchedText(searchQuery);
    setIsSearching(false);
  }, [boards]);

  // Debounced search (only in search mode)
  useEffect(() => {
    if (isPartnrMode) return;
    const timeout = setTimeout(() => {
      searchItems(searchText);
    }, 200);
    return () => clearTimeout(timeout);
  }, [searchText, searchItems, isPartnrMode]);

  // Cmd/Ctrl+K focuses the bar from anywhere
  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== 'k') return;
      e.preventDefault();
      if (isPartnrMode) {
        composeRef.current?.focus();
      } else {
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [isPartnrMode]);

  const getItemIcon = (type: string) => {
    switch (type) {
      case 'sticky': return StickyNote;
      case 'text_card': return Type;
      case 'document': return FileText;
      case 'image': return Image;
      case 'file': return File;
      case 'link': return LinkIcon;
      default: return FileText;
    }
  };

  const stripHtml = (html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  const getItemPreview = (item: Item): string => {
    const content = item.content as Record<string, unknown>;
    switch (item.item_type) {
      case 'sticky':
        return (content.text as string) || 'Empty sticky note';
      case 'text_card':
        return (content.title as string) || stripHtml((content.body as string) || '') || 'Empty text card';
      case 'document':
        return (content.title as string) || 'Untitled document';
      case 'image':
        return (content.alt as string) || 'Image';
      case 'file':
        return (content.filename as string) || 'File';
      case 'link':
        return getLinkResultTitle(item);
      default:
        return 'Item';
    }
  };

  const addToCanvas = async (item: SearchResult) => {
    if (!currentBoardId) {
      toast.error('Open a board first.');
      return;
    }

    const viewportCenterX = (window.innerWidth / 2) / zoom - panX;
    const viewportCenterY = (window.innerHeight / 2) / zoom - panY;
    const position = {
      x: Math.round(viewportCenterX / 20) * 20,
      y: Math.round(viewportCenterY / 20) * 20,
    };

    // If the item is from a different board, show duplicate/sync choice
    if (item.board_id && item.board_id !== currentBoardId) {
      setSyncModalState({ item, position });
      setSelectedItem(null);
      return;
    }

    const result = await createItem({
      board_id: currentBoardId,
      item_type: item.item_type,
      position_x: position.x,
      position_y: position.y,
      width: item.width,
      height: item.height,
      content: item.content as Record<string, unknown>,
    });

    if (result) {
      setIsResultsOpen(false);
      setQuery('');
      setSelectedItem(null);
    }
  };

  const handleSearchDuplicate = async () => {
    if (!syncModalState || !currentBoardId) return;
    const { item, position } = syncModalState;

    await createItem({
      board_id: currentBoardId,
      item_type: item.item_type,
      position_x: position.x,
      position_y: position.y,
      width: item.width,
      height: item.height,
      content: item.content as Record<string, unknown>,
    });

    setSyncModalState(null);
    setIsResultsOpen(false);
    setQuery('');
  };

  const handleSearchSync = async () => {
    if (!syncModalState || !currentBoardId) return;
    const { item, position } = syncModalState;

    await createItem({
      board_id: currentBoardId,
      item_type: 'synced_item' as any,
      position_x: position.x,
      position_y: position.y,
      width: item.width,
      height: item.height,
      content: {
        source_item_id: item.id,
        source_board_id: item.board_id,
      },
    });

    setSyncModalState(null);
    setIsResultsOpen(false);
    setQuery('');
  };

  /** Open a board result: step inside it */
  const openBoardResult = async ({ board }: BoardSearchResult) => {
    setIsResultsOpen(false);
    setQuery('');
    setSelectedItem(null);
    inputRef.current?.blur();
    if (board.id === currentBoardId) return;
    setViewMode('focus');
    await navigateToBoard(board);
  };

  /** Show where a board's folder card sits, centred and selected on its parent board */
  const showBoardFolder = ({ folder }: BoardSearchResult) => {
    if (!folder) return;
    const onThisBoard = folder.board_id === currentBoardId
      ? items.find(i => i.id === folder.id)
      : undefined;
    if (onThisBoard) {
      handleGoToItem(onThisBoard);
      return;
    }
    setIsResultsOpen(false);
    setQuery('');
    inputRef.current?.blur();
    void openItemOnCanvas(folder);
  };

  const goToOriginal = async (item: SearchResult) => {
    const board = boards.find(b => b.id === item.board_id);
    if (board) {
      setViewMode('focus');
      await navigateToBoard(board);
      setIsResultsOpen(false);
      setQuery('');
      setSelectedItem(null);
    }
  };

  const handlePartnrSubmit = async () => {
    if (!query.trim() || !currentBoardId || partnrBusy) return;

    if (partnrV2) {
      usePartnrPanelStore.getState().open({
        newChat: true,
        message: query,
        selectionIds: selectedItems.map(item => item.id),
        searchContext: searchEscalationContext ?? undefined,
      });
      setQuery('');
      setSearchEscalationContext(null);
      return;
    }

    const chat = await createChat(currentBoardId, selectedItems, query);
    if (chat) {
      setQuery('');
    }
  };

  /**
   * Send straight to the side chat. Partnr-selected and canvas-selected items
   * go along as context; an empty message opens a fresh chat.
   */
  const askPartnr = async (message: string) => {
    if (partnrBusy) return;
    if (!currentBoardId) {
      toast.error('Navigate to a board first');
      return;
    }

    const canvasItems = hasCanvasSelection
      ? items.filter(item => canvasSelectedItemIds.has(item.id) && !partnrSelectedItemIds.has(item.id))
      : [];
    const context = [...selectedItems, ...canvasItems].slice(0, 5);

    // Hand the search the user was looking at to Partnr, as Tab escalation did
    const searchHandoff = message && !isPartnrMode && resultCount !== undefined
      ? { originalQuery: message, resultCount, resultItemIds: results.slice(0, 4).map(r => r.id) }
      : null;

    setIsResultsOpen(false);
    if (partnrV2) {
      // The panel creates the chat row and sends; an empty message just opens a fresh chat
      usePartnrPanelStore.getState().open({
        newChat: true,
        message: message || undefined,
        selectionIds: context.map(item => item.id),
        searchContext: searchHandoff ?? searchEscalationContext ?? undefined,
      });
      setSearchEscalationContext(null);
      setQuery('');
      setIntentOverride(null);
      clearPartnrSelection();
      inputRef.current?.blur();
      return;
    }
    if (searchHandoff) setSearchEscalationContext(searchHandoff);
    const chat = await createChat(currentBoardId, context, message || undefined);
    if (!chat) {
      setSearchEscalationContext(null);
      return;
    }
    setQuery('');
    setIntentOverride(null);
    clearPartnrSelection();
    inputRef.current?.blur();
  };

  /** Canvas-selection row: ask now if there's a question, else open the compose box with the items */
  const handleAskPartnrAboutSelection = () => {
    if (query.trim()) {
      askPartnr(detected.text);
      return;
    }
    if (canvasSelectionCount === 0 || !currentBoardId) return;
    const canvasItems = items.filter(item => canvasSelectedItemIds.has(item.id));
    if (canvasItems.length === 0) return;

    // Transfer canvas selection to Partnr selection
    clearPartnrSelection();
    for (const item of canvasItems.slice(0, 5)) {
      togglePartnrItemSelection(item.id);
    }

    setPartnrMode(true);
    setIsResultsOpen(false);
  };

  const toggleIntent = () => {
    setIntentOverride({ intent: barIntent === 'partnr' ? 'search' : 'partnr', over: autoIntent });
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (!value.trim()) setIntentOverride(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.nativeEvent.isComposing) return;

    // Escape exits Partnr mode back to search
    if (e.key === 'Escape' && isPartnrMode) {
      e.preventDefault();
      setPartnrMode(false);
      setSearchEscalationContext(null);
      return;
    }

    // Shift+Enter adds a line in the compose box
    if (e.key === 'Enter' && isPartnrMode && !e.shiftKey) {
      e.preventDefault();
      handlePartnrSubmit();
      return;
    }

    if (isPartnrMode) return;

    // Tab flips between Search and Ask Partnr
    if (e.key === 'Tab' && !e.shiftKey && showIntentPill) {
      e.preventDefault();
      toggleIntent();
      return;
    }

    if (e.key !== 'Enter' || !query.trim()) return;
    e.preventDefault();

    // Cmd/Ctrl+Enter always asks Partnr; plain Enter does what the pill says
    if (isPartnrBetaEnabled && (e.metaKey || e.ctrlKey || barIntent === 'partnr')) {
      askPartnr(detected.text);
      return;
    }

    setIsResultsOpen(true);
    searchItems(searchText);
  };

  const handleRemoveSelectedItem = (itemId: string) => {
    togglePartnrItemSelection(itemId);
  };

  /** Escalate with results: switch to Partnr mode with current results as context */
  const handleEscalateToPartnrWithResults = () => {
    // Store escalation context
    setSearchEscalationContext({
      originalQuery: query,
      resultCount: results.length,
      resultItemIds: results.slice(0, 4).map(r => r.id),
    });
    
    // Switch to Partnr mode
    setPartnrMode(true);

    // Don't auto-select items — let the user choose which items to reference
    clearPartnrSelection();

    // Clear the input so user can type a new prompt about the results
    setQuery('');

    // Close search results
    setIsResultsOpen(false);
  };

  /** Handle clicking a suggestion chip */
  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    composeRef.current?.focus();
  };

  /** Create a sticky note from search query when no results */
  const handleCreateFromSearch = async (searchQuery: string) => {
    if (!currentBoardId) {
      toast.error('Navigate to a board first');
      return;
    }
    
    const centerX = (window.innerWidth / 2) / zoom - panX;
    const centerY = (window.innerHeight / 2) / zoom - panY;
    
    await createItem({
      board_id: currentBoardId,
      item_type: 'sticky',
      position_x: Math.round(centerX / 20) * 20,
      position_y: Math.round(centerY / 20) * 20,
      width: 200,
      height: 150,
      content: { text: searchQuery },
    });
    
    setIsResultsOpen(false);
    setQuery('');
  };

  /** Select a result that is on the current board and scroll to it */
  const handleGoToItem = (searchResult: SearchResult) => {
    clearSelection();
    selectItem(searchResult.id, false);

    // Pan to center the item in viewport
    const centerX = -(searchResult.position_x + searchResult.width / 2) + (window.innerWidth / 2) / zoom;
    const centerY = -(searchResult.position_y + searchResult.height / 2) + (window.innerHeight / 2) / zoom;
    setPan(centerX, centerY);

    setIsResultsOpen(false);
    setQuery('');
  };

  /** Add or remove a result from Partnr's AI context */
  const handleTogglePartnrContext = (searchResult: SearchResult) => {
    const wasSelected = partnrSelectedItemIds.has(searchResult.id);
    togglePartnrItemSelection(searchResult.id);
  };

  // Dynamic placeholder based on search context
  const partnrPlaceholder = searchEscalationContext
    ? `Ask about "${searchEscalationContext.originalQuery.substring(0, 30)}${searchEscalationContext.originalQuery.length > 30 ? '...' : ''}"...`
    : "Ask Partnr anything...";

  // Selected items as numbered chips (Partnr mode)
  const selectedPills = selectedItems.length > 0 ? (
    <div className="flex flex-wrap gap-1.5">
      {selectedItems.map((item, index) => (
        <div
          key={item.id}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-partnr-static text-partnr-foreground shadow-sm"
        >
          <span
            className="w-4 h-4 rounded-full bg-white/90 text-[10px] flex items-center justify-center font-bold"
            style={{ color: 'hsl(var(--partnr))' }}
          >
            {index + 1}
          </span>
          <span className="max-w-[100px] truncate">{getItemPreviewText(item)}</span>
          <button
            onClick={() => handleRemoveSelectedItem(item.id)}
            aria-label="Remove from context"
            className="hover:text-partnr-foreground/70"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
      <button
        onClick={clearPartnrSelection}
        className="px-2 py-1 rounded-full text-xs text-muted-foreground hover:text-foreground hover:bg-muted"
      >
        Clear all
      </button>
    </div>
  ) : null;

  // Prompt suggestions from the search that escalated into Partnr
  const suggestionChips = escalationSuggestions && partnrSelectedItemIds.size > 0 ? (
    <>
      <p className="text-[10px] text-muted-foreground/60 mb-1.5 uppercase tracking-wider font-medium">
        Based on your search
      </p>
      <div className="flex flex-wrap gap-1">
        {escalationSuggestions.map((suggestion) => (
          <button
            key={suggestion}
            onClick={() => handleSuggestionClick(suggestion)}
            className="text-[11px] px-2.5 py-1 rounded-full border border-partnr/20 hover:border-partnr/40 hover:bg-partnr/5 text-muted-foreground hover:text-foreground transition-all truncate max-w-[200px]"
          >
            {suggestion}
          </button>
        ))}
      </div>
    </>
  ) : null;

  // Inside the open compose box: suggestions until typing starts, then the context chips
  const showSuggestionsInBox = suggestionChips && !query.trim();
  const composeHeader = showSuggestionsInBox || selectedPills ? (
    <div className="space-y-2">
      {showSuggestionsInBox && <div>{suggestionChips}</div>}
      {selectedPills}
    </div>
  ) : null;

  return (
    <TooltipProvider>
      <div ref={containerRef} className="relative">
        <div className={cn(
          "relative flex items-center gap-1 rounded-full transition-all duration-300",
          isPartnrMode && "partnr-glow ring-1 ring-partnr/30",
          isPartnrMode && selectedItems.length > 0 && "partnr-glow-active",
          // A signal ring when canvas items are selected (Partnr can be activated)
          !isPartnrMode && hasCanvasSelection && "ring-1 ring-signal"
        )}>
          {/* Partnr mode keeps its own leading mark; search mode draws the lens inside Seek */}
          {isPartnrMode && (
          <div className={cn(
            "relative flex items-center justify-center h-9 w-9 rounded-full transition-all duration-300",
            isPartnrMode 
              ? "bg-partnr text-partnr-foreground shadow-partnr partnr-glow-active" 
              : hasCanvasSelection
                ? "bg-partnr-static text-partnr-foreground"
                : showIntentPill && barIntent === 'partnr'
                  ? "bg-transparent text-partnr"
                  : "bg-transparent text-muted-foreground"
          )}
          >
            {isPartnrMode || hasCanvasSelection || (showIntentPill && barIntent === 'partnr') ? (
              <PartnrMark size={16} />
            ) : (
              <Search className="h-4 w-4" />
            )}
            {/* Selection count badge when canvas items are selected */}
            {!isPartnrMode && hasCanvasSelection && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-partnr-secondary text-[10px] text-partnr-foreground flex items-center justify-center font-bold shadow-sm">
                {canvasSelectionCount}
              </span>
            )}
            {/* Search context indicator */}
            {searchEscalationContext && isPartnrMode && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-partnr-secondary border-2 border-background" />
            )}
          </div>
          )}

          {/* Input Field with AnimatePresence */}
          <AnimatePresence mode="wait">
            {isPartnrMode ? (
              <motion.div
                key="partnr-mode"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.2 }}
                className="relative flex-1"
              >
                <PartnrComposeBox
                  value={query}
                  onChange={setQuery}
                  onKeyDown={handleKeyDown}
                  onSubmit={handlePartnrSubmit}
                  placeholder={partnrPlaceholder}
                  isStreaming={partnrBusy}
                  expanded={isComposeOpen}
                  onExpandedChange={setIsComposeOpen}
                  textareaRef={composeRef}
                  header={composeHeader}
                  trailing={
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={partnrV2 ? () => usePartnrPanelStore.getState().openHistory() : toggleHistoryPanel}
                          aria-label="Chat history"
                          className={cn(
                            "text-muted-foreground hover:text-partnr p-0.5 transition-colors",
                            isHistoryOpen && "text-partnr"
                          )}
                        >
                          <History className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        Chat History
                      </TooltipContent>
                    </Tooltip>
                  }
                />
              </motion.div>
            ) : (
              <motion.div
                key="search-mode"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reduceMotion ? 0 : 0.15 }}
                className="relative"
              >
                <motion.div
                  data-seek
                  data-open={seekOpen}
                  // A dock button while shut; once open it is a field and holds still
                  data-dock-item={seekOpen ? 'off' : ''}
                  className={cn(
                    "relative h-10 rounded-full transition-colors duration-150 ease-out motion-reduce:transition-none",
                    seekOpen ? "bg-background/50" : "hover:bg-muted/60"
                  )}
                  initial={false}
                  animate={{ width: seekOpen ? SEEK_OPEN : SEEK_SHUT, scale: seekPressed ? 0.88 : 1 }}
                  transition={reduceMotion ? { duration: 0 } : SEEK_SPRING}
                >
                  {/* The field, clipped to the box so it is revealed by the box growing */}
                  <div className="absolute inset-0 overflow-hidden rounded-full">
                    <Input
                      ref={inputRef}
                      value={query}
                      tabIndex={seekOpen ? 0 : -1}
                      onChange={(e) => {
                        handleQueryChange(e.target.value);
                        setIsResultsOpen(true);
                        markTyping();
                      }}
                      onFocus={() => {
                        setSeekFocused(true);
                        setIsResultsOpen(true);
                      }}
                      onBlur={() => setSeekFocused(false)}
                      onKeyDown={(e) => {
                        // Escape empties and closes the field
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          handleQueryChange('');
                          setResults([]);
                          setBoardResults([]);
                          setIsResultsOpen(false);
                          inputRef.current?.blur();
                          return;
                        }
                        handleKeyDown(e);
                      }}
                      placeholder={
                        hasCanvasSelection
                          ? `Ask Partnr about ${canvasSelectionCount} item${canvasSelectionCount > 1 ? 's' : ''}...`
                          : isPartnrBetaEnabled ? 'Search or ask Partnr…' : 'Search your mindfull...'
                      }
                      aria-label="Search or ask Partnr"
                      className={cn(
                        "absolute left-0 top-0 h-10 text-sm rounded-full border-none bg-transparent pl-10 focus-visible:ring-0 focus-visible:ring-offset-0",
                        "transition-opacity duration-150 ease-out motion-reduce:transition-none",
                        showIntentPill ? "pr-[136px]" : query ? "pr-8" : "pr-3",
                        // The placeholder and caret arrive once there is room for them
                        seekOpen ? "opacity-100 delay-75" : "opacity-0"
                      )}
                      style={{ width: SEEK_OPEN }}
                    />
                  </div>

                  {/* The lens: a fixed inset, centred when shut and aligned when open */}
                  <div
                    aria-hidden
                    data-dock-glyph
                    className={cn(
                      "pointer-events-none absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-full",
                      "will-change-transform transition-[color,background-color,transform] duration-150 ease-out motion-reduce:transition-none",
                      hasCanvasSelection
                        ? "bg-partnr-static text-partnr-foreground"
                        : showIntentPill && barIntent === 'partnr'
                          ? "text-partnr"
                          : seekOpen ? "text-muted-foreground" : "text-foreground"
                    )}
                  >
                    {hasCanvasSelection || (showIntentPill && barIntent === 'partnr') ? (
                      <PartnrMark size={16} />
                    ) : (
                      <svg
                        viewBox="0 0 18 18"
                        className="h-4 w-4 fill-none stroke-current transition-[stroke-width] duration-150 motion-reduce:transition-none"
                        style={{ strokeWidth: seekTyping ? 2.4 : 2, strokeLinecap: 'round' }}
                      >
                        <circle cx="7.6" cy="7.6" r="5.4" />
                        <path d="M11.6 11.6 L15.4 15.4" />
                      </svg>
                    )}
                    {/* Selection count badge when canvas items are selected */}
                    {hasCanvasSelection && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-partnr-secondary text-[10px] text-partnr-foreground flex items-center justify-center font-bold shadow-sm">
                        {canvasSelectionCount}
                      </span>
                    )}
                  </div>

                  {/* The way in, only while shut: once open, a target over the field would eat the caret click */}
                  {!seekOpen && (
                    <button
                      type="button"
                      aria-label="Search"
                      onClick={openSeek}
                      className="absolute inset-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal"
                    />
                  )}

                  {/* Search Action Buttons */}
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1" style={{ opacity: seekOpen ? 1 : 0, pointerEvents: seekOpen ? undefined : 'none' }}>
                    {showIntentPill && (
                      <button
                        type="button"
                        // Keep focus in the input so typing carries on
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={toggleIntent}
                        data-intent={barIntent}
                        aria-label={`Enter will ${barIntent === 'partnr' ? 'ask Partnr' : 'search'}. Click or press Tab to switch.`}
                        title="Tab to switch"
                        className={cn(
                          "relative h-6 rounded-full px-2 text-[11px] font-medium whitespace-nowrap overflow-hidden transition-colors duration-150 motion-reduce:transition-none",
                          barIntent === 'partnr'
                            ? "bg-partnr/10 text-partnr hover:bg-partnr/20"
                            : "bg-muted text-muted-foreground hover:text-foreground"
                        )}
                      >
                        <AnimatePresence mode="popLayout" initial={false}>
                          <motion.span
                            key={barIntent}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: reduceMotion ? 0 : 0.12 }}
                            className="flex items-center gap-1"
                          >
                            {barIntent === 'partnr' && <PartnrMark size={12} />}
                            {barIntent === 'partnr' ? '↵ Ask Partnr' : '↵ Search'}
                          </motion.span>
                        </AnimatePresence>
                      </button>
                    )}
                    {query && (
                      <button
                        type="button"
                        onClick={() => {
                          handleQueryChange('');
                          setResults([]);
                          setBoardResults([]);
                          inputRef.current?.focus();
                        }}
                        aria-label="Clear"
                        className="text-muted-foreground hover:text-foreground p-0.5"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Selected items and search suggestions float above the pill; the open compose box shows them inside */}
        {isPartnrMode && !isComposeOpen && selectedPills && (
          <div className="absolute bottom-full left-0 mb-3 w-max max-w-[360px]">{selectedPills}</div>
        )}

        {isPartnrMode && !isComposeOpen && suggestionChips && (
          <div className="absolute bottom-full left-0 mb-14 w-[360px]">
            <div className="bg-popover/95 backdrop-blur-sm border border-border/50 rounded-lg shadow-lg px-3 py-2.5">
              {suggestionChips}
            </div>
          </div>
        )}

        {/* Search Results Panel */}
        {!isPartnrMode && isResultsOpen && (query || results.length > 0 || hasCanvasSelection) && (
          <div className="absolute bottom-full left-0 mb-2 w-[360px] max-h-[400px] overflow-auto bg-popover border border-border rounded-lg shadow-lg">
            {/* Ask Partnr about canvas-selected items */}
            {hasCanvasSelection && (
              <button
                onClick={handleAskPartnrAboutSelection}
                className="w-full flex items-center gap-3 px-4 py-3 bg-partnr/10 hover:bg-partnr/20 border-b border-partnr/10 transition-colors group"
              >
                <div className="w-8 h-8 rounded-full bg-partnr/20 flex items-center justify-center shrink-0 group-hover:bg-partnr/30 transition-colors">
                  <PartnrMark size={16} className="text-partnr" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    Ask Partnr about {canvasSelectionCount === 1 ? 'this item' : `these ${canvasSelectionCount} items`}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {query.trim() ? `"${query}"` : 'Type a question or press Enter'}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-partnr opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            )}

            {isSearching ? (
              <div className="p-4 text-center text-muted-foreground">
                Searching...
              </div>
            ) : results.length === 0 && boardResults.length === 0 && query.trim().length > 0 ? (
              <div className="px-4 py-6 text-center">
                <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <Search className="h-5 w-5 text-muted-foreground/50" />
                </div>
                <p className="text-sm text-muted-foreground mb-1">No results for "{query}"</p>
                <div className="flex flex-col items-center gap-2 mt-3">
                  <button
                    onClick={() => askPartnr(detected.text)}
                    className="inline-flex items-center gap-1.5 text-sm text-partnr hover:text-partnr/80 font-medium transition-colors"
                  >
                    <PartnrMark size={14} />
                    {hasCanvasSelection ? `Ask Partnr about ${canvasSelectionCount} selected item${canvasSelectionCount > 1 ? 's' : ''}` : 'Ask Partnr about this'}
                  </button>
                  <button
                    onClick={() => handleCreateFromSearch(query)}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <StickyNote className="h-3 w-3" />
                    Create a sticky note instead
                  </button>
                </div>
              </div>
            ) : results.length > 0 || boardResults.length > 0 ? (
              <>
                {boardResults.length > 0 && (
                  <div className="p-2 pb-0 space-y-1" role="group" aria-label="Boards">
                    <p className="px-2 pt-1 text-[10px] font-medium uppercase tracking-wider text-ink-3">
                      Boards
                    </p>
                    {boardResults.map((result) => {
                      const { board, path, folder } = result;
                      const palette = getFolderColorFromValue(result.color);
                      const where = path.length > 0 ? `In ${path.join(' › ')}` : 'Your home board';
                      return (
                        <div
                          key={board.id}
                          className="group flex items-center gap-1 rounded-md transition-colors duration-150 ease-out motion-reduce:transition-none hover:bg-wash"
                        >
                          <button
                            type="button"
                            onClick={() => void openBoardResult(result)}
                            aria-label={`Open ${board.name}, ${where}`}
                            className="flex flex-1 min-w-0 items-center gap-3 p-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal"
                          >
                            <span
                              aria-hidden
                              className="w-12 h-12 rounded flex items-center justify-center shrink-0"
                              style={{ backgroundColor: palette.folder }}
                            >
                              <Folder className="h-5 w-5" style={{ color: palette.labelText }} />
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-medium text-ink truncate">{board.name}</span>
                              <span className="block text-xs text-ink-3 truncate mt-0.5">{where}</span>
                            </span>
                          </button>

                          {folder && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 mr-2 shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity motion-reduce:transition-none"
                              onClick={() => showBoardFolder(result)}
                              title="Show the folder on its board"
                              aria-label={`Show where ${board.name} is`}
                            >
                              <LocateFixed className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {results.length > 0 && (
                  <div className="p-2 space-y-1">
                    {boardResults.length > 0 && (
                      <p className="px-2 pt-1 text-[10px] font-medium uppercase tracking-wider text-ink-3">
                        Items
                      </p>
                    )}
                    {results.map((item) => {
                      const Icon = getItemIcon(item.item_type);
                      const preview = getItemPreview(item);
                      const content = item.content as Record<string, unknown>;
                      const thumbnail = getSearchResultThumbnail(item);
                      const isOnCurrentBoard = item.board_id === currentBoardId;
                      const isInPartnrContext = partnrSelectedItemIds.has(item.id);
                      
                      return (
                        <div
                          key={item.id}
                          className="group flex items-start gap-3 p-2 rounded-md hover:bg-muted cursor-pointer"
                          onClick={() => setSelectedItem(item)}
                        >
                          {thumbnail ? (
                            <div className="w-12 h-12 rounded overflow-hidden shrink-0 bg-muted">
                              <img 
                                src={thumbnail} 
                                alt="" 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div 
                              className={cn(
                                "w-12 h-12 rounded flex items-center justify-center shrink-0",
                                item.item_type === 'sticky' 
                                  ? `bg-${(content.color as string) || 'yellow'}-200`
                                  : 'bg-muted'
                              )}
                              style={item.item_type === 'sticky' ? {
                                backgroundColor: `var(--sticky-${(content.color as string) || 'yellow'}, hsl(var(--muted)))`
                              } : undefined}
                            >
                              <Icon className="h-5 w-5 text-muted-foreground" />
                            </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{preview}</p>
                          {item.boardName && (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <div 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: item.boardColor || 'hsl(var(--primary))' }}
                              />
                              <span className="text-xs text-muted-foreground truncate">
                                {item.boardName}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="icon"
                            variant="ghost"
                            className={cn("h-8 w-8", isInPartnrContext && "text-partnr opacity-100")}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePartnrContext(item);
                            }}
                            title={isInPartnrContext ? 'Remove from Partnr context' : 'Add to Partnr context'}
                            aria-label={isInPartnrContext ? 'Remove from Partnr context' : 'Add to Partnr context'}
                          >
                            <PartnrMark size={14} />
                          </Button>
                          {isOnCurrentBoard && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGoToItem(item);
                              }}
                              title="Go to item"
                              aria-label="Go to item"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCanvas(item);
                            }}
                            title={isOnCurrentBoard ? 'Add a copy to this board' : 'Add to this board'}
                            aria-label={isOnCurrentBoard ? 'Add a copy to this board' : 'Add to this board'}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}

                {/* Ask Partnr about results footer */}
                {results.length > 0 && (
                  <div className="border-t border-border/50 px-4 py-2.5">
                    <button
                      onClick={handleEscalateToPartnrWithResults}
                      className="w-full flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-partnr transition-colors py-1"
                    >
                      <PartnrMark size={12} />
                      <span>Ask Partnr about these {results.length} results</span>
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* Chat History Panel */}
        {isPartnrMode && isHistoryOpen && !partnrV2 && (
          <PartnrChatHistory />
        )}

        {/* Item Preview Modal (portaled: the toolbar's transformed ancestors
            would otherwise make `fixed` relative to the toolbar, not the viewport) */}
        {selectedItem && createPortal(
          <div 
            ref={previewModalRef}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50"
            onClick={() => setSelectedItem(null)}
          >
            <div 
              className="bg-popover border border-border rounded-lg shadow-xl max-w-lg w-full mx-4 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{getItemPreview(selectedItem)}</h3>
                  {selectedItem.boardName && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <div 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: selectedItem.boardColor || 'hsl(var(--primary))' }}
                      />
                      <span className="text-sm text-muted-foreground">
                        {selectedItem.boardName}
                      </span>
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedItem(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="mb-6">
                {selectedItem.item_type === 'image' && (selectedItem.content as Record<string, unknown>).url && (
                  <img 
                    src={(selectedItem.content as Record<string, unknown>).url as string}
                    alt=""
                    className="w-full h-auto max-h-[300px] object-contain rounded-md"
                  />
                )}
                {(selectedItem.item_type === 'sticky' || selectedItem.item_type === 'text_card') && (
                  <p className="text-muted-foreground">
                    {(selectedItem.content as Record<string, unknown>).text as string ||
                     stripHtml((selectedItem.content as Record<string, unknown>).body as string || '') ||
                     'No content'}
                  </p>
                )}
                {isTweetItem(selectedItem) ? (
                  <div className="max-h-[260px] overflow-auto rounded-md border border-border">
                    <TweetPreviewCard item={selectedItem} />
                  </div>
                ) : selectedItem.item_type === 'link' && (
                  <div className="space-y-2">
                    {getSearchResultThumbnail(selectedItem) && (
                      <img
                        src={getSearchResultThumbnail(selectedItem) as string}
                        alt=""
                        className="w-full h-auto max-h-[240px] object-cover rounded-md"
                      />
                    )}
                    {(selectedItem.content as Record<string, unknown>).description && (
                      <p className="text-muted-foreground line-clamp-4">
                        {(selectedItem.content as Record<string, unknown>).description as string}
                      </p>
                    )}
                  </div>
                )}
                {selectedItem.item_type === 'document' && (
                  <p className="text-muted-foreground line-clamp-6">
                    {stripHtml((selectedItem.content as Record<string, unknown>).body as string || '') || 'No content'}
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => goToOriginal(selectedItem)}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Go to Original
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => addToCanvas(selectedItem)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Canvas
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Sync Choice Modal for search results from other boards */}
      {syncModalState && (
        <SyncChoiceModal
          isOpen={true}
          onClose={() => setSyncModalState(null)}
          itemName={getItemPreview(syncModalState.item)}
          sourceBoardName={syncModalState.item.boardName || 'another board'}
          onDuplicate={handleSearchDuplicate}
          onSync={handleSearchSync}
        />
      )}
    </TooltipProvider>
  );
}
